import React, { useState, useEffect } from 'react'
import { toastError, toastSuccess, toastLoading, toastDismiss } from '../../utils/toastHelper'
import { useTheme } from '../../hooks/useTheme'
import { useApiClient } from '../../hooks/useApiClient'
import { useEditarPago, QUERY_KEYS } from '../../hooks/usePagosAdmin'
import { useQueryClient } from '@tanstack/react-query'

import type { Pago } from '../../hooks/usePagosAdmin'
import ComprobanteViewer from '../common/ComprobanteViewer'

interface EditarPagoModalProps {
  isOpen: boolean
  pago: Pago | null
  onClose: () => void
  onSuccess: () => void
}

const EditarPagoModal: React.FC<EditarPagoModalProps> = ({
  isOpen,
  pago,
  onClose,
  onSuccess,
}) => {
  const { colors } = useTheme()
  const { apiClient } = useApiClient()
  const queryClient = useQueryClient()
  const [monto, setMonto] = useState<number>(0)
  const [metodoPago, setMetodoPago] = useState<string>('')
  const [referenciaPago, setReferenciaPago] = useState<string>('')
  const [comprobante, setComprobante] = useState<File | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isDeletingComprobante, setIsDeletingComprobante] = useState(false)
  const editarPagoMutation = useEditarPago(pago?.reserva_id || '')

  useEffect(() => {
    if (pago) {
      setMonto(pago.monto)
      // Extraer metodo y referencia de metadatos
      setMetodoPago(pago.metadatos?.metodo || '')
      setReferenciaPago(pago.metadatos?.referencia || '')
      setComprobante(null)
    }
  }, [pago, isOpen])

  if (!isOpen || !pago) return null

  // ✅ Solo permitir editar pagos manuales
  const esManual = pago.proveedor_pago === 'manual'

  const handleGuardarCambios = async () => {
    if (!esManual) {
      toastError('Solo se pueden editar pagos manuales')
      return
    }
    if (monto <= 0) {
      toastError('El monto debe ser mayor a 0')
      return
    }

    if (!metodoPago) {
      toastError('El método de pago es obligatorio')
      return
    }

    setIsLoading(true)
    try {
      // Actualizar pago con todos los campos
      await editarPagoMutation.mutateAsync({
        pagoId: pago.id,
        datos: {
          monto,
          metodo_pago: metodoPago,
          referencia_pago: referenciaPago || null,
        },
      })

      // Si hay comprobante nuevo, subirlo
      if (comprobante) {
        try {
          const formData = new FormData()
          formData.append('file', comprobante)

          await apiClient.post(
            `/api/admin-protected/pagos/${pago.id}/subir-comprobante`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              }
            }
          )

          toastSuccess('Comprobante subido exitosamente!')
          // Refrescar datos después de subir comprobante
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.PAGOS_ADMIN],
          })
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.PAGOS_BY_RESERVA, pago.reserva_id],
          })
        } catch (uploadError) {
          console.error('Error subiendo comprobante:', uploadError)
          toastError('Pago actualizado pero no se pudo subir el comprobante.')
        }
      }

      onSuccess()
      onClose()
    } catch (error) {
      console.error('Error actualizando pago:', error)
      // El error ya será mostrado por el hook useEditarPago
    } finally {
      setIsLoading(false)
    }
  }

  const handleEliminarComprobante = async () => {
    if (
      !window.confirm(
        '¿Estás seguro que deseas eliminar el comprobante? Esta acción no se puede deshacer.'
      )
    ) {
      return
    }

    setIsDeletingComprobante(true)
    try {
      await apiClient.delete(`/api/admin-protected/pagos/${pago.id}/eliminar-comprobante`)
      toastSuccess('Comprobante eliminado exitosamente!')
      // Refrescar datos después de eliminar comprobante
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.PAGOS_ADMIN],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.PAGOS_BY_RESERVA, pago.reserva_id],
      })
      onSuccess()
      onClose()
    } catch (error) {
      console.error('Error eliminando comprobante:', error)
      toastError('Error al eliminar el comprobante')
    } finally {
      setIsDeletingComprobante(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full"
        style={{ borderTop: `4px solid ${colors.primary}` }}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            ✏️ Editar Pago #{pago.id}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6 space-y-6">
          {/* ✅ Advertencia si no es manual */}
          {!esManual && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-3">
                <span className="text-2xl">⚠️</span>
                <div>
                  <h3 className="text-sm font-bold text-red-900 mb-1">
                    Pago de proveedor {pago.proveedor_pago}
                  </h3>
                  <p className="text-xs text-red-700">
                    Solo los pagos manuales pueden editarse. Los pagos de Getnet y Khipu no se pueden modificar desde esta interfaz.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Monto */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              💰 Monto *
            </label>
            <input
              type="number"
              value={monto}
              onChange={(e) => setMonto(parseFloat(e.target.value) || 0)}
              disabled={!esManual}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              style={{ focusOutlineColor: colors.primary }}
              step="0.01"
              min="0"
            />
          </div>

          {/* Método de Pago */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              💳 Método de Pago *
            </label>
            <select
              value={metodoPago}
              onChange={(e) => setMetodoPago(e.target.value)}
              disabled={!esManual}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              style={{ focusOutlineColor: colors.primary }}
            >
              <option value="">Selecciona un método</option>
              <option value="transferencia">Transferencia Bancaria</option>
              <option value="efectivo">Efectivo</option>
              <option value="cheque">Cheque</option>
              <option value="tarjeta">Tarjeta</option>
            </select>
          </div>

          {/* Referencia de Pago */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              📝 Referencia / Número de Operación
            </label>
            <input
              type="text"
              value={referenciaPago}
              onChange={(e) => setReferenciaPago(e.target.value)}
              disabled={!esManual}
              placeholder="Ej: N° transferencia, N° boleta, etc."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              style={{ focusOutlineColor: colors.primary }}
            />
          </div>

          {/* Comprobante actual */}
          {pago.comprobante_path && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                ✓ Comprobante Actual
              </h3>
              <div className="space-y-3">
                <ComprobanteViewer
                  pagoId={pago.id}
                  filename={pago.comprobante_filename}
                  mimeType={pago.comprobante_mime_type}
                  size={pago.comprobante_size}
                  uploadDate={pago.comprobante_upload_date}
                  showMetadata={true}
                />
                <button
                  onClick={handleEliminarComprobante}
                  disabled={isDeletingComprobante}
                  className="w-full px-3 py-2 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeletingComprobante ? '⏳...' : '🗑️ Eliminar Comprobante'}
                </button>
              </div>
            </div>
          )}

          {/* Nuevo comprobante */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              📎 {pago.comprobante_path ? 'Cambiar' : 'Cargar'} Comprobante
            </label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx"
              onChange={(e) => {
                const file = e.target.files?.[0] || null
                if (file) {
                  // Validar tamaño (máx 10MB)
                  if (file.size > 10 * 1024 * 1024) {
                    toastError('El archivo no puede ser mayor a 10MB')
                    return
                  }
                  // Validar tipo
                  const typesPermitidos = [
                    'application/pdf',
                    'image/jpeg',
                    'image/png',
                    'image/gif',
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                  ]
                  if (!typesPermitidos.includes(file.type)) {
                    toastError('Tipo de archivo no permitido. Usa PDF, JPG, PNG, GIF o DOC.')
                    return
                  }
                }
                setComprobante(file)
              }}
              className="w-full px-4 py-2 border-2 border-dashed rounded-lg focus:ring-2 focus:border-transparent"
              style={{
                borderColor: comprobante ? colors.primary : colors.border,
                backgroundColor: comprobante ? `${colors.primary}08` : 'transparent',
              }}
            />
            <p className="text-xs text-gray-500 mt-2">
              Tipos permitidos: PDF, JPG, PNG, GIF, DOC (máx 10MB)
            </p>
            {comprobante && (
              <div className="mt-3 p-3 rounded-lg flex items-center justify-between" style={{
                backgroundColor: `${colors.primary}12`,
                borderLeft: `3px solid ${colors.primary}`,
              }}>
                <span className="text-xs font-medium text-gray-700">
                  ✓ {comprobante.name}
                </span>
                <button
                  onClick={() => setComprobante(null)}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex gap-2 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleGuardarCambios}
            disabled={isLoading || !esManual}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: colors.primary,
            }}
            onMouseEnter={(e) => {
              if (!isLoading && esManual) {
                e.currentTarget.style.opacity = '0.9'
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading && esManual) {
                e.currentTarget.style.opacity = '1'
              }
            }}
          >
            {!esManual ? '⚠️ No Editable' : isLoading ? '⏳ Guardando...' : '✓ Guardar Cambios'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default EditarPagoModal
